import { useLocalStorage } from '@vueuse/core'
import {
  STORAGE_KEY_ACCOUNT,
  STORAGE_KEY_CURRENT_USER_HANDLE,
  STORAGE_KEY_INVITATION,
  CACHE_KEY_SOURCE
} from '@/constants'
import {
  USER_PROFILE,
  USER_SEND_CODE,
  netClient,
  type CommonResponse,
  USER_REGISTER,
  USER_MODIFY_PASSWORD,
  USER_LOGOUT,
  USER_SET_NAME,
  USER_REFRESH_TOKEN,
  USER_LOGIN,
  USER_LOGIN_BY_CODE,
  USER_SET_LANG,
  USER_SET_AVATAR,
  USER_BIND_PHONE,
  USER_CHANGE_PHONE,
  USER_SET_PASSWORD,
  USER_SET_SOURCE,
  USER_DESTROY_ACCOUNT
} from '@/apis/api'
import { sha256 } from 'js-sha256'
import { handleErr } from '../exception/handler'
import { CODE_OK } from '@/apis/code'
import type { FileDTO } from '../media/media'
import { computed, effectScope, ref, type EffectScope, type Ref } from 'vue'
import type { MembershipDTO } from '../member/member'
import { isEmpty, isNotEmpty } from '@/utils/validate'
import { isInWechat } from '@/utils/device'
import { renderMessage } from '@/utils/render'
import { toExternalLink } from '@/utils/routerShim'
import { ElMessage } from 'element-plus'
import i18n from '@/utils/i18n'

const _sourceCloseOnce = useLocalStorage<boolean>(CACHE_KEY_SOURCE, false)
export const sourceCloseOnce = _sourceCloseOnce

export const DEMO_ID = '10000'
const currentUserHandleLocal: Ref<string> = useLocalStorage<string>(STORAGE_KEY_CURRENT_USER_HANDLE, DEMO_ID)

let _clientHandleRef: Ref<string> | null = null

function getHandleCookie() {
  const nuxtApp = tryUseNuxtApp()
  if (!nuxtApp) {
    const nullRef = ref<string>(DEMO_ID)
    return computed({
      get: () => nullRef.value,
      set: (val) => {
        nullRef.value = val
      }
    })
  }
  return useCookie<string>(STORAGE_KEY_CURRENT_USER_HANDLE, {
    default: () => DEMO_ID,
    path: '/',
    sameSite: 'lax',
    secure: false,
    maxAge: 60 * 60 * 24 * 7
  })
}

export const currentUserHandle: Ref<string> = computed({
  get() {
    if (import.meta.server) {
      return getHandleCookie().value || DEMO_ID
    }
    if (!_clientHandleRef) {
      const cookieRef = getHandleCookie()
      _clientHandleRef = computed({
        get: () =>
          currentUserHandleLocal.value !== DEMO_ID ? currentUserHandleLocal.value : cookieRef.value || DEMO_ID,
        set: (val) => {
          currentUserHandleLocal.value = val
          cookieRef.value = val
        }
      }) as Ref<string>
    }
    return _clientHandleRef.value
  },
  set(val) {
    console.log('!debug!调用 set 方法', val)
    if (import.meta.server) {
      console.log('!debug!调用 set 方法 服务端', val)
      getHandleCookie().value = val
      return
    }
    // console.log("!debug!调用 set 方法 客户端", val)
    // currentUserHandleLocal.value = val
    // getHandleCookie().value = val
    // console.log("!debug!调用 set 方法 客户端 结束", getHandleCookie().value,currentUserHandleLocal.value )
    if (!_clientHandleRef) {
      console.log('!debug!调用 set 方法 客户端 未缓存', val)
      const _ = (currentUserHandle as any).effect // 触发初始化
      currentUserHandleLocal.value = val
      getHandleCookie().value = val
      return
    }
    console.log('!debug!调用 set 方法 客户端 已缓存', val)
    _clientHandleRef.value = val
  }
}) as Ref<string>
export const isLogin = computed(() => {
  if (currentUserHandle.value == DEMO_ID) {
    return false
  }
  const token = getTokenCookie().value?.accessToken
  return !!token && token !== '<DEMO_KEY>'
})

/**
 * 处理从微信小程序跳转到免登录页面时，引导去登录，并清除相关 query
 */
export const showRedirectToLoginDialog = ref(false)
export const loginDialogTitle = ref('')
export const loginDialogCanClose = ref(true)
export const checkLoginGuidance = (title: string) => {
  console.log('!!debug!! 进入方法')
  if (import.meta.server) return
  const route = useRoute()
  const router = useRouter()
  const { fromMiniApp, redirect, ...rest } = route.query as Record<string, any>
  if (!isLogin.value) {
    console.log('!!debug!! 触发准备登录')
    showRedirectToLoginDialog.value = true
    loginDialogTitle.value = title
    loginDialogCanClose.value = false
  } else {
    console.log('!!debug!! 登录成功 清除 url')

    router.replace({
      path: route.path,
      query: rest,
      hash: route.hash
    })
  }
}

export const isSetRealName = computed(() => {
  console.log('isSetRealName', currentUser.value?.user?.name?.fullName)
  return (
    isNotEmpty(currentUser.value?.user?.name?.fullName) &&
    currentUser.value?.user?.name?.fullName?.startsWith('人生一页用户') === false
  )
})

import { tryUseNuxtApp } from '#app'

const AUTH_COOKIE_NAME = 'auth_token'

// 微信小程序 webview 中 cookie 可能不持久或不随请求发送，因此客户端需要以 localStorage 为回退。
// 见：https://developers.weixin.qq.com/community/develop/doc/0004c2451f86b8524fb2e93246b000
let _clientTokenRef: Ref<Token | null> | null = null

export function clearAuthCookieDirect() {
  if (typeof document !== 'undefined') {
    document.cookie = `${AUTH_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0`
  }
}

function getTokenCookie() {
  const nuxtApp = tryUseNuxtApp()
  if (!nuxtApp) {
    const nullRef = ref<Token | null>(null)
    return computed({
      get: () => nullRef.value,
      set: (val) => {
        nullRef.value = val
        if (val === null) {
          clearAuthCookieDirect()
        }
      }
    })
  }
  // 服务端：仅使用 cookie（SSR 时请求头里的 Cookie）
  if (import.meta.server) {
    return useCookie<Token | null>(AUTH_COOKIE_NAME, {
      default: () => null,
      path: '/',
      sameSite: 'lax',
      secure: false,
      maxAge: 60 * 60 * 24 * 7
    })
  }
  // 客户端：cookie 优先，若 cookie 为空则从 localStorage 回退（兼容微信小程序 webview 等 cookie 失灵场景）
  if (!_clientTokenRef) {
    const cookieRef = useCookie<Token | null>(AUTH_COOKIE_NAME, {
      default: () => null,
      path: '/',
      sameSite: 'lax',
      secure: false,
      maxAge: 60 * 60 * 24 * 7
    })
    const storageRef = getUserStorage()
    _clientTokenRef = computed({
      get: () => cookieRef.value ?? storageRef.value?.token ?? null,
      set: (v) => {
        cookieRef.value = v
        if (v != null) {
          const g = storageRef.value
          if (g) storageRef.value = { ...g, token: v }
        }
      }
    })
  }
  return _clientTokenRef
}

// We keep the main user object in local storage for detailed profile data, but token comes from cookie for SSR.
// Only use the cached userStorage on client side or within the proxy scope
const getUserStorage = () => useUserLocalStorage<AccountDTO>(STORAGE_KEY_ACCOUNT, getDemoUser)

export const currentUser = computed({
  get() {
    const tc = getTokenCookie()
    const val = getUserStorage().value
    const isDemoUser = currentUserHandle.value == DEMO_ID

    const isServer = typeof window === 'undefined'

    if (isServer) {
      console.log('[SSR currentUser] Token flow:', {
        cookieToken: tc.value?.accessToken ? `${tc.value.accessToken.substring(0, 20)}...` : 'NULL',
        storageToken: val.token?.accessToken ? `${val.token.accessToken.substring(0, 20)}...` : 'NULL',
        isDemoUser
      })
    }

    if (isDemoUser) {
      return {
        user: null as any,
        token: null as any,
        member: null as any
      } as AccountDTO
    }

    // Logged-in user: prefer cookie token for SSR consistency
    if (tc.value) {
      val.token = tc.value
    }

    // Client-side sync: if storage has a real token but cookie doesn't, sync it
    if (!isServer && val.token && !tc.value) {
      console.log('[currentUser] Syncing token from storage to cookie')
      tc.value = val.token
    }

    return val
  },
  set(val: AccountDTO) {
    getUserStorage().value = val
    if (val && val.token) {
      const cookie = getTokenCookie()
      // Only write to cookie if the token has actually changed.
      // This prevents "Cannot append headers" errors in Nuxt SWR/SSR when
      // redundant writes happen after response is sent.
      const currentToken = cookie.value
      if (
        !currentToken ||
        currentToken.accessToken !== val.token.accessToken ||
        currentToken.refreshToken !== val.token.refreshToken
      ) {
        cookie.value = val.token
      }
    }
  }
})

function getDemoUser(): AccountDTO {
  return {
    user: null as any,
    token: null as any,
    member: null as any
  } as AccountDTO
}

export const getCurrentUser = () => currentUser

export function saveUser(account: AccountDTO) {
  console.log('currentUser:', currentUser, account)
  if (currentUser != null && account != null) {
    currentUserHandle.value = account.user.id
    console.log('即将被保存 的 account', account)
    // Trigger setter by assigning a new object
    currentUser.value = {
      user: account.user,
      token: account.token,
      member: account.member
    } as AccountDTO
    console.log('保存后的currentUser', currentUser.value)
  } else {
    console.error('current user is null')
  }
}

export function saveUserInfoDTO(userInfo: UserInfoDTO) {
  if (currentUser.value != null && userInfo != null) {
    if (currentUser.value.user?.id == userInfo.id) {
      currentUser.value = {
        ...currentUser.value,
        user: userInfo
      }
    } else {
      console.error('save invalid user Info:', userInfo)
    }
  } else {
    console.error('current user is null')
  }
}

export function saveUserInfo(userInfo: UserInfoDTO | null, member: MembershipDTO | null) {
  if (currentUser.value != null && userInfo != null) {
    if (currentUser.value.user?.id == userInfo.id) {
      const newMember = member ? { ...member, capUsage: member.capUsage } : currentUser.value.member
      currentUser.value = {
        ...currentUser.value,
        user: userInfo,
        member: newMember
      }
    } else {
      console.error('save invalid user Info:', userInfo)
    }
  } else {
    console.error('current user is null')
  }
}

export function saveToken(token: Token) {
  console.log('saveToken', token)
  if (currentUser != null && token != null) {
    currentUser.value.token = token
    getTokenCookie().value = token
  } else {
    console.error('current user is null')
  }
}

export interface WechatUserInfo {
  openId: string
  unionId: string
  nickname: string
  wechatAvatar: string
  fromPc: boolean
  miniOpenId: string
}

export interface Phone {
  countryCode: string
  phoneNumber: string
}

export interface UserInfoDTO {
  fullName(fullName: any): unknown
  id: string
  name: Name
  nickname: string
  email: string
  phone: Phone
  wechatUserInfo: WechatUserInfo
  avatar: FileDTO
  state: number
  createdAt: number
  hasPassword: boolean
  source: string
  subscribed: boolean
  loginByWechat: boolean // 是微信登录
}

export interface UserMinDTO {
  id: string
  name: Name
  source: string
  wechatUserInfo: WechatUserInfo
  avatar: FileDTO
  createdAt: number
  subscribed: boolean
  loginByWechat: boolean
}

export interface AccountDTO {
  user: UserInfoDTO
  token: Token
  member: MembershipDTO
}

export interface Token {
  accessToken: string
  refreshToken: string
}

export function localLogout() {
  currentUserHandle.value = DEMO_ID
  console.log('!debug!!localLogout', currentUserHandle.value, DEMO_ID)

  sourceCloseOnce.value = false
  getTokenCookie().value = null
  clearAuthCookieDirect()
  console.log('after logout user:', currentUser.value.token)
}

export function removeUserFromLocalStorage(userId: string) {
  try {
    const allAccounts = useLocalStorage<Record<string, AccountDTO>>(STORAGE_KEY_ACCOUNT, {}, { deep: true })
    if (allAccounts.value && allAccounts.value[userId]) {
      delete allAccounts.value[userId]
    }
    localLogout()
  } catch (error) {
    console.error('删除用户本地存储信息失败:', error)
  }
}

export function isWechatBound(isLogin: boolean = true) {
  if (
    isInWechat() &&
    (getCurrentUser()?.value.user.wechatUserInfo == null || isEmpty(getCurrentUser()?.value.user.wechatUserInfo.openId))
  ) {
    navigateTo('/wechatLogin?bind=true')
    ElMessage({
      message: renderMessage(i18n.global.t(isLogin ? 'login.please_bind_wechat' : 'login.please_bind_wechat_logged')),
      type: 'warning'
    })
    return false
  }
  return true
}

// export function useCurrentUser(): Ref<AccountDTO> | null {
//   const accounts = globalThis.localStorage.getItem(STORAGE_KEY_ACCOUNT)
//   if (accounts == null || accounts.length == 0) {
//     return null
//   } else {
//     return ref(accounts[currentUserHandle.value])
//   }
// }

interface UseUserLocalStorageCache {
  scope: EffectScope
  value: Ref<Record<string, any>>
}

/**
 * Create reactive storage for the current user
 */
export function useUserLocalStorage<T extends object>(key: string, initial: () => T): Ref<T> {
  if (import.meta.server) {
    const all = useLocalStorage<Record<string, T>>(key, {}, { deep: true })
    if (!all.value[currentUserHandle.value]) {
      all.value[currentUserHandle.value] = initial()
    }
    return computed({
      get: () => all.value[currentUserHandle.value],
      set: (val) => {
        all.value[currentUserHandle.value] = val
      }
    }) as Ref<T>
  }

  // @ts-expect-error bind value to the function
  const map: Map<string, UseUserLocalStorageCache> = (useUserLocalStorage._ = useUserLocalStorage._ || new Map())

  if (!map.has(key)) {
    const scope = effectScope(true)
    const value = scope.run(() => {
      const all = useLocalStorage<Record<string, T>>(key, {}, { deep: true, listenToStorageChanges: false })

      return computed({
        get() {
          const id = currentUserHandle.value
          const defaults = initial()
          if (!all.value[id]) {
            all.value[id] = defaults
          } else {
            all.value[id] = Object.assign(initial(), all.value[id] || {})
          }
          return all.value[id]
        },
        set(val) {
          const id = currentUserHandle.value
          all.value[id] = val
        }
      })
    })
    map.set(key, { scope, value: value! })
  }

  return map.get(key)!.value as Ref<T>
}

// ======== 尝试更新token ==========
export const tryRefreshToken = (refreshToken: string) =>
  netClient.request<CommonResponse<Token>>(USER_REFRESH_TOKEN, 'post', { refreshToken })

// ======== 发送验证码 ==========
export interface SendCodeBody {
  phone: string // 手机号
  type: number // 验证码类型 1 - 注册, 2 - 验证码登录, 3 - 找回密码
  sceneId: string
  captchaVerifyParam: string
}

export function sendVerifyCode(body: SendCodeBody) {
  return netClient.request<CommonResponse<unknown>>(USER_SEND_CODE, 'post', body)
}

// ======== 注册 ==========
export class RegisterBody {
  private _code: string
  private _phone: string
  // TODO: 加盐校验后再存储
  private _passwd: string

  constructor(phone: string, code: string, passwd: string) {
    this._code = code
    this._phone = phone
    this._passwd = passwd
  }

  public toMap(): Record<string, any> {
    const result: Record<string, any> = {
      phone: this._phone,
      code: this._code,
      passwd: sha256(this._passwd)
    }
    return result
  }
}

export function register(body: RegisterBody) {
  return netClient.request<CommonResponse<AccountDTO>>(USER_REGISTER, 'post', body.toMap())
}

// ======== 验证码登录 ==========
export interface LoginByCodeBody {
  phone: string
  code: string
}

export function loginByCode(body: LoginByCodeBody) {
  return netClient.request<CommonResponse<AccountDTO>>(USER_LOGIN_BY_CODE, 'post', body)
}

// ======== 密码登录 ==========
export class LoginBody {
  private _phone: string
  private _passwd: string

  constructor(phone: string, passwd: string) {
    this._phone = phone
    this._passwd = passwd
  }

  toMap(): Record<string, any> {
    const result: Record<string, any> = {
      phone: this._phone,
      passwd: sha256(this._passwd)
    }
    return result
  }
}

export function login(body: LoginBody) {
  return netClient.request<CommonResponse<AccountDTO>>(USER_LOGIN, 'post', body.toMap())
}

// ======== 设置密码 ==========
export class setPasswordBody {
  private _newPasswd: string

  constructor(passwd: string) {
    this._newPasswd = passwd
  }
  toMap(): Record<string, any> {
    const result: Record<string, any> = {
      newPasswd: sha256(this._newPasswd)
    }
    return result
  }
}

// ======== 绑定手机号 ========
export function bindPhoneAndPassword(phone: string, code: string, passwd: string) {
  return netClient.request<CommonResponse<UserInfoDTO>>(USER_BIND_PHONE, 'post', {
    phone: phone,
    code: code,
    passwd: sha256(passwd)
  })
}

export function changePhone(phone: string, code: string) {
  return netClient.request<CommonResponse<UserInfoDTO>>(USER_CHANGE_PHONE, 'post', {
    phone: phone,
    code: code
  })
}

export function setPassword(body: setPasswordBody) {
  return netClient.request<CommonResponse<any>>(USER_SET_PASSWORD, 'post', body.toMap())
}

// ======== 修改密码 ==========
export class ModifyPasswordBody {
  _phone: string
  _code: string
  _passwd: string

  constructor(phone: string, code: string, password: string) {
    this._phone = phone
    this._code = code
    this._passwd = password
  }

  toMap() {
    const result: Record<string, any> = {
      phone: this._phone,
      code: this._code,
      passwd: sha256(this._passwd)
    }
    return result
  }
}

export function modifyPassword(body: ModifyPasswordBody) {
  return netClient.request<CommonResponse<AccountDTO>>(USER_MODIFY_PASSWORD, 'post', body.toMap())
}

// ======== 登出 ==========
export interface LogoutBody {}

export function logout(body: LogoutBody) {
  return netClient.request<CommonResponse<unknown>>(USER_LOGOUT, 'post', body)
}

// ======== 设置名称 ==========
export interface Name {
  fullName: string
  firstName: string
  middleName: string
  familyName: string
}

export function setName(body: Name) {
  return netClient.request<CommonResponse<AccountDTO>>(USER_SET_NAME, 'post', body)
}

export function setAvatar(fileId: string) {
  return netClient.request<CommonResponse<FileDTO>>(USER_SET_AVATAR, 'post', { fileId: fileId })
}

// ======== 设置语言  ======
export function setLang(lang: string) {
  return netClient.request<CommonResponse<AccountDTO>>(USER_SET_LANG, 'post', { lang: lang })
}

// ======== 获取详细资料 ==========
export function getUserProfile() {
  // Align return type with other APIs: wraps payload in CommonResponse
  return netClient.request<CommonResponse<AccountDTO>>(USER_PROFILE, 'get', {})
}

export async function fetchUserProfile() {
  const { data, error, statusCode } = await getUserProfile()
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as unknown as Ref<CommonResponse<any>>
    if (resData.value.code === CODE_OK) {
      // console.log('get profile success')
      const account: AccountDTO = (data as unknown as Ref<CommonResponse<AccountDTO>>).value.data
      saveUserInfo(account.user, account.member)

      if (account.user && isEmpty(account.user?.name?.fullName) && !isInWechat() && account.user?.id) {
        navigateTo('/setRealname')
        ElMessage({
          message: renderMessage(i18n.global.t('set_real_name.please_set_realname')),
          type: 'warning'
        })
      }
    }
  }
}

export const lastInvitation: Ref<string> = useLocalStorage<string>(STORAGE_KEY_INVITATION, '')

export async function setUserSource(source: string) {
  return netClient.request<CommonResponse<number>>(USER_SET_SOURCE, 'post', { source: source })
}

export interface DestroyAccountCommand {
  userId: string
}

export function destroyAccount(body: DestroyAccountCommand) {
  return netClient.request<CommonResponse<unknown>>(USER_DESTROY_ACCOUNT, 'post', body)
}
